/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import utils from '../../utils';
import { HiTrash } from 'react-icons/hi';
import FormError from '../feedback/FormError';
import adminServices from '../../services/AdminServices';

function PaymentModal({ action, activePayment, onReload, setActivePayment }) {
    const [purpose, setPurpose] = useState('Service Payment');
    const [status, setStatus] = useState('Pending');
    const [paymentMethod, setPaymentMethod] = useState('MPESA');
    const [disbursementStatus, setDisbursementStatus] = useState('Pending');
    const [total, setTotal] = useState('');
    const [nurseAmount, setNurseAmount] = useState('');
    const [platformAmount, setPlatformAmount] = useState('');
    const [transactionReference, setTransactionReference] = useState('');
    const [notes, setNotes] = useState('');
    const [hasConfirmed, setHasConfirmed] = useState(false);

    const [ownerId, setOwnerId] = useState('');
    const [nurseId, setNurseId] = useState('');
    const [requestId, setRequestId] = useState('');

    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    const tabs = useMemo(() => {
        const baseTabs = ['Info'];
        if (action === 1) {
            return [...baseTabs, 'Edit', 'Delete'];
        }
        return baseTabs;
    }, [action]);

    useEffect(() => {
        if (action === 1 && activePayment) {
            setPurpose(activePayment.purpose || 'Service Payment');
            setStatus(activePayment.status || 'Pending');
            setPaymentMethod(activePayment.payment_method || 'MPESA');
            setDisbursementStatus(activePayment.disbursement_status || 'Pending');
            setTotal(activePayment.total?.toString() || '');
            setNurseAmount(activePayment.nurse_amount?.toString() || '');
            setPlatformAmount(activePayment.platform_amount?.toString() || '');
            setTransactionReference(activePayment.transaction_reference || '');
            setNotes(activePayment.notes || '');
            setHasConfirmed(activePayment.has_confirmed || false);
            setOwnerId(activePayment.owner?.id || '');
            setNurseId(activePayment.nurse?.id || '');
            setRequestId(activePayment.nurse_request?.id || '');
        } else {
            resetForm();
        }
    }, [action, activePayment]);

    useEffect(() => {
        if (total) {
            const totalNum = parseFloat(total) || 0;
            const platform = (totalNum * 0.15).toFixed(2);
            const nurse = (totalNum - platform).toFixed(2);
            setPlatformAmount(platform);
            setNurseAmount(nurse);
        }
    }, [total]);

    const resetForm = () => {
        setPurpose('Service Payment');
        setStatus('Pending');
        setPaymentMethod('MPESA');
        setDisbursementStatus('Pending');
        setTotal('');
        setNurseAmount('');
        setPlatformAmount('');
        setTransactionReference('');
        setNotes('');
        setHasConfirmed(false);
        setOwnerId('');
        setNurseId('');
        setRequestId('');
        setActiveTab(0);
        setError(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        const closeBtn = document.getElementById('payment-modal-close-btn');
        setLoading(true);

        const payload = {
            purpose,
            status,
            payment_method: paymentMethod,
            disbursement_status: purpose === 'Nurse Disbursement' ? disbursementStatus : undefined,
            total: parseFloat(total) || 0,
            nurse_amount: parseFloat(nurseAmount) || 0,
            platform_amount: parseFloat(platformAmount) || 0,
            transaction_reference: transactionReference,
            notes,
            has_confirmed: hasConfirmed,
            owner: purpose === 'Service Payment' ? parseInt(ownerId) || null : null,
            nurse: purpose === 'Nurse Disbursement' ? parseInt(nurseId) || null : null,
            nurse_request: parseInt(requestId) || null,
        };

        let request;
        if (action === 1) {
            request = adminServices.updatePayment(activePayment.id, payload);
        } else {
            request = adminServices.createPayment(payload);
        }

        request
            .then((res) => {
                const successMsg = action === 1 ? 'Payment Updated Successfully' : 'Payment Created Successfully';
                toast.success(successMsg);
                setLoading(false);
                onReload?.();
                closeBtn?.click();
                resetForm();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setError(err);
                setLoading(false);
            });
    };

    const handleDelete = () => {
        const closeBtn = document.getElementById('payment-modal-close-btn');
        setDeleteLoading(true);

        adminServices.deletePayment(activePayment.id)
            .then((res) => {
                toast.success('Payment Deleted Successfully');
                setDeleteLoading(false);
                onReload?.();
                closeBtn?.click();
                resetForm();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setDeleteLoading(false);
            });
    };

    const changeTab = (idx) => {
        if (action === 1) {
            setActiveTab(idx);
        }
    };

    const formatCurrency = (value) => {
        if (!value) return '';
        return `KES ${parseFloat(value).toLocaleString('en-KE')}`;
    };

    const getStatusBadge = (status) => {
        const styles = {
            Pending: 'badge-warning',
            Paid: 'badge-success',
            Failed: 'badge-error',
            Refunded: 'badge-ghost',
        };
        return styles[status] || 'badge-ghost';
    };

    const getPurposeBadge = (purpose) => {
        return purpose === 'Service Payment' ? 'badge-info' : 'badge-accent';
    };

    return (
        <div>
            <label htmlFor="payment-modal" id="payment-open-btn" className="hidden">
                Add / Update Payment
            </label>
            <input type="checkbox" id="payment-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-11/12 max-w-3xl">
                    <label
                        onClick={resetForm}
                        htmlFor="payment-modal"
                        id="payment-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </label>

                    <h2 className="text-lg font-bold mb-3">
                        {action === 1 ? 'Edit' : 'Add'}
                        <span> Payment{action === 1 && activePayment && ':'}</span>
                        {action === 1 && activePayment && (
                            <span className='text-gray-500 ml-2'>#{activePayment?.id}</span>
                        )}
                    </h2>

                    <div className="flex justify-center mb-6">
                        <div className="border-gray-200">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx
                                            ? 'tab-active bg-primary text-white rounded-lg'
                                            : ''
                                            }`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {activeTab === 0 && action === 1 && activePayment && (
                        <div className="border border-border-light bg-surface-light p-4 shadow-sm rounded-xl mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className='font-bold text-base mb-2'>Payment Overview</p>
                                    <div className="space-y-1.5">
                                        <p>
                                            <span className="font-medium mr-2">Purpose:</span>
                                            <span className={`badge ${getPurposeBadge(activePayment.purpose)} badge-sm`}>
                                                {activePayment.purpose}
                                            </span>
                                        </p>
                                        <p>
                                            <span className="font-medium mr-2">Status:</span>
                                            <span className={`badge ${getStatusBadge(activePayment.status)} badge-sm`}>
                                                {activePayment.status}
                                            </span>
                                        </p>
                                        <p>
                                            <span className="font-medium mr-2">Method:</span>
                                            {activePayment.payment_method}
                                        </p>
                                        {activePayment.purpose === 'Nurse Disbursement' && (
                                            <p>
                                                <span className="font-medium mr-2">Disbursement:</span>
                                                <span className={`badge badge-sm ${activePayment.disbursement_status === 'Paid' ? 'badge-success' : 'badge-warning'}`}>
                                                    {activePayment.disbursement_status}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p className='font-bold text-base mb-2'>Amounts</p>
                                    <div className="space-y-1.5">
                                        <p>
                                            <span className="font-medium mr-2">Total:</span>
                                            <span className="font-semibold">{formatCurrency(activePayment.total)}</span>
                                        </p>
                                        <p>
                                            <span className="font-medium mr-2">Nurse Amount:</span>
                                            <span className="text-success font-semibold">{formatCurrency(activePayment.nurse_amount)}</span>
                                        </p>
                                        <p>
                                            <span className="font-medium mr-2">Platform Amount:</span>
                                            <span className="text-zinc-600 font-semibold">{formatCurrency(activePayment.platform_amount)}</span>
                                        </p>
                                        <p>
                                            <span className="font-medium mr-2">Reference:</span>
                                            <span className="font-mono text-xs">{activePayment.transaction_reference || '—'}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="md:col-span-2 pt-2 border-t border-border-light">
                                    <p className='font-bold text-base mb-2'>Linked Entities</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                        {activePayment.purpose === 'Service Payment' && activePayment.owner && (
                                            <p>
                                                <span className="font-medium mr-2">Patient:</span>
                                                {activePayment.owner.user?.full_name || activePayment.owner.email}
                                            </p>
                                        )}
                                        {activePayment.purpose === 'Nurse Disbursement' && activePayment.nurse && (
                                            <p>
                                                <span className="font-medium mr-2">Nurse:</span>
                                                {activePayment.nurse.user?.full_name} ({activePayment.nurse.license_number})
                                            </p>
                                        )}
                                        {activePayment.nurse_request && (
                                            <p>
                                                <span className="font-medium mr-2">Request:</span>
                                                #{activePayment.nurse_request.id} - {activePayment.nurse_request.patient?.user?.full_name}
                                            </p>
                                        )}
                                        {activePayment.creation_time && (
                                            <p>
                                                <span className="font-medium mr-2">Created:</span>
                                                {new Date(activePayment.creation_time).toLocaleDateString('en-KE', {
                                                    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                                })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === (action === 1 ? 1 : 0) && (
                        <form onSubmit={handleSubmit}>
                            {error && <FormError error={error} />}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Purpose */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Purpose *</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={purpose}
                                        onChange={(e) => setPurpose(e.target.value)}
                                        disabled={action === 1}
                                    >
                                        <option value="Service Payment">Service Payment</option>
                                        <option value="Nurse Disbursement">Nurse Disbursement</option>
                                    </select>
                                </div>

                                {/* Status */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Status *</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                    >
                                        <option value="Pending">Pending</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Failed">Failed</option>
                                        <option value="Refunded">Refunded</option>
                                    </select>
                                </div>

                                {/* Payment Method */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Payment Method *</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                    >
                                        <option value="MPESA">MPESA</option>
                                        <option value="Card">Card</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                    </select>
                                </div>

                                {/* Disbursement Status (only for Nurse Disbursement) */}
                                {purpose === 'Nurse Disbursement' && (
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Disbursement Status</span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={disbursementStatus}
                                            onChange={(e) => setDisbursementStatus(e.target.value)}
                                        >
                                            <option value="Pending">Pending</option>
                                            <option value="Processing">Processing</option>
                                            <option value="Paid">Paid</option>
                                            <option value="Failed">Failed</option>
                                        </select>
                                    </div>
                                )}

                                {/* Total Amount */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Total Amount (KES) *</span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="input input-bordered w-full"
                                        value={total}
                                        onChange={(e) => setTotal(e.target.value)}
                                        required
                                        placeholder="0.00"
                                    />
                                </div>

                                {/* Transaction Reference */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Transaction Reference</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        value={transactionReference}
                                        onChange={(e) => setTransactionReference(e.target.value)}
                                        placeholder="e.g. MPESA123ABC"
                                    />
                                </div>
                            </div>

                            {/* Auto-calculated amounts display */}
                            {total && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-3 bg-base-200 rounded-lg">
                                    <div>
                                        <p className="text-xs text-base-content/70">Platform Amount (15%)</p>
                                        <p className="font-semibold text-zinc-700">{formatCurrency(platformAmount)}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-base-content/70">Nurse Amount (85%)</p>
                                        <p className="font-semibold text-success">{formatCurrency(nurseAmount)}</p>
                                    </div>
                                </div>
                            )}

                            {/* Entity Selection */}
                            <div className="mt-4 pt-4 border-t border-border-light">
                                <p className="font-medium mb-3">Link to Entity</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {purpose === 'Service Payment' ? (
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Patient</span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered w-full"
                                                value={ownerId}
                                                onChange={(e) => setOwnerId(e.target.value)}
                                                placeholder="Enter Patient User ID"
                                            />
                                        </div>
                                    ) : (
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Nurse</span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered w-full"
                                                value={nurseId}
                                                onChange={(e) => setNurseId(e.target.value)}
                                                placeholder="Enter Nurse Profile ID"
                                            />
                                        </div>
                                    )}
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text">Nurse Request (Optional)</span>
                                        </label>
                                        <input
                                            type="number"
                                            className="input input-bordered w-full"
                                            value={requestId}
                                            onChange={(e) => setRequestId(e.target.value)}
                                            placeholder="Enter Request ID"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="form-control mt-4">
                                <label className="label">
                                    <span className="label-text">Notes</span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered w-full h-20"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Additional notes about this payment..."
                                />
                            </div>

                            {/* Confirm Checkbox */}
                            <div className="form-control mt-4">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-primary"
                                        checked={hasConfirmed}
                                        onChange={(e) => setHasConfirmed(e.target.checked)}
                                    />
                                    <span className="label-text">Mark as confirmed</span>
                                </label>
                            </div>

                            <div className="mt-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                >
                                    {loading ? (
                                        <span>{action === 1 ? 'Updating...' : 'Creating...'}</span>
                                    ) : (
                                        <span>{action === 1 ? 'Update Payment' : 'Create Payment'}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 2 && action === 1 && (
                        <div className="bg-white rounded-lg shadow-sm border border-border-light overflow-hidden mt-6">
                            <div className="p-6 md:p-8">
                                <div className="border-l-4 border-error pl-4 py-2">
                                    <h3 className="font-bold text-error">Danger Zone</h3>
                                    <p className="text-sm text-gray-600 mt-2">
                                        Deleting this payment record is irreversible. This action cannot be undone.
                                    </p>
                                    <div className="pt-4">
                                        {deleteLoading ? (
                                            <div className="flex justify-center py-2">
                                                <span className="loading loading-spinner loading-sm"></span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                            >
                                                <HiTrash className="h-4 w-4" />
                                                <span>Delete Payment #{activePayment?.id}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PaymentModal;